// TemporalGraph.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

import {
  Box,
  Typography,
  Paper,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Tooltip,
  styled,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Skeleton,
  useTheme,
  useMediaQuery,
  Chip,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Menu,
  Portal
} from '@mui/material';

import {
  ShowChartRounded as ShowChartRoundedIcon,
  CalendarMonthRounded as CalendarMonthRoundedIcon,
  LayersRounded as LayersRoundedIcon,
  WarningAmberRounded as WarningAmberRoundedIcon,
  InsightsRounded as InsightsRoundedIcon,
  CheckRounded as CheckRoundedIcon,
  InfoOutlined as InfoOutlinedIcon,
  CloseFullscreenRounded as CloseFullscreenRoundedIcon,
  OpenInFullRounded as OpenInFullRoundedIcon,
  RestartAltRounded as RestartAltRoundedIcon,
  CloseRounded as CloseRoundedIcon,
  FullscreenExitRounded as FullscreenExitRoundedIcon,
  Thunderstorm as ThunderstormIcon,
} from '@mui/icons-material';

import { alpha } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';

import { format, subDays } from 'date-fns';

// Registrar módulos de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ChartTitle,
  ChartTooltip,
  Legend,
  Filler
);

/* ================= Helpers de datos ================= */

const transformForChart = (data, mapLeftLayer) => {
  return data.map(item => ({
    ...item,
    HITS_rel: typeof item.HITS_rel === 'string'
      ? parseFloat((parseFloat(item.HITS_rel.replace('%', '')) * 100).toFixed(1))
      : parseFloat((item.HITS_rel * 100).toFixed(1)),
    MISSES_rel: typeof item.MISSES_rel === 'string'
      ? parseFloat((parseFloat(item.MISSES_rel.replace('%', '')) * 100).toFixed(1))
      : parseFloat((item.MISSES_rel * 100).toFixed(1)),
    FAR: item.FAR,
    GSS: item.GSS,
    F1: item.F1,
    RECALL: item.RECALL,
    PRECISION: item.PRECISION,
    STORM_FREQUENCY:
      mapLeftLayer === 'CNN_HISTORICAL:EU_025_live_demo'
        ? (item.TP_abs + item.FN_abs) / 46529
        : mapLeftLayer === 'CNN_HISTORICAL:EU_01_live_demo'
          ? (item.TP_abs + item.FN_abs) / 54233
          : 0,
    HITS_rel_n1m: typeof item.HITS_rel_n1m === 'string'
      ? parseFloat((parseFloat(item.HITS_rel_n1m.replace('%', '')) * 100).toFixed(1))
      : parseFloat((item.HITS_rel_n1m * 100).toFixed(1)),
    MISSES_rel_n1m: typeof item.MISSES_rel_n1m === 'string'
      ? parseFloat((parseFloat(item.MISSES_rel_n1m.replace('%', '')) * 100).toFixed(1))
      : parseFloat((item.MISSES_rel_n1m * 100).toFixed(1)),
    FAR_n1m: item.FAR_n1m,
    GSS_n1m: item.GSS_n1m,
    F1_n1m: item.F1_n1m,
    RECALL_n1m: item.RECALL_n1m,
    PRECISION_n1m: item.PRECISION_n1m,
    STORM_FREQUENCY_n1m:
      mapLeftLayer === 'CNN_HISTORICAL:EU_025_live_demo'
        ? (item.TP_abs_n1m + item.FN_abs_n1m) / 46529
        : mapLeftLayer === 'CNN_HISTORICAL:EU_01_live_demo'
          ? (item.TP_abs_n1m + item.FN_abs_n1m) / 54233
          : 0,
  }));
};

const pivotChartData = (data, metricKey, cqlFechaMax) => {
  const expectedIssues = [
    { label: "D-2 01 UTC", hourUTC: 1, offsetDays: 2 },
    { label: "D-2 07 UTC", hourUTC: 7, offsetDays: 2 },
    { label: "D-2 13 UTC", hourUTC: 13, offsetDays: 2 },
    { label: "D-2 19 UTC", hourUTC: 19, offsetDays: 2 },
    { label: "D-1 01 UTC", hourUTC: 1, offsetDays: 1 },
    { label: "D-1 07 UTC", hourUTC: 7, offsetDays: 1 },
    { label: "D-1 13 UTC", hourUTC: 13, offsetDays: 1 },
    { label: "D-1 19 UTC", hourUTC: 19, offsetDays: 1 },
    { label: "D-0 01 UTC", hourUTC: 1, offsetDays: 0 },
    { label: "D-0 07 UTC", hourUTC: 7, offsetDays: 0 },
    { label: "D-0 13 UTC", hourUTC: 13, offsetDays: 0 },
    { label: "D-0 19 UTC", hourUTC: 19, offsetDays: 0 }
  ];

  const fixedHours = Array.from({ length: 24 }, (_, h) => ({
    hour: h,
    hourLabel: `${String(h).padStart(2, '0')}:00`
  }));

  const issueDataMap = {};
  data.forEach(row => {
    const hour = new Date(row.VALID_TIMESTAMP).getUTCHours();
    const issueTimestamp = row.ISSUE_TIMESTAMP;
    if (!issueDataMap[issueTimestamp]) issueDataMap[issueTimestamp] = {};
    issueDataMap[issueTimestamp][hour] = row[metricKey];
  });

  const palette = [
    // D-2 (Azules) – más contraste
    '#0b2e7a', '#1e40af', '#3b82f6', '#93c5fd',

    // D-1 (Verdes/Teal)
    '#065f46', '#059669', '#10b981', '#6ee7b7',

    // D-0 (Naranjas/Rojos)
    '#991b1b', '#dc2626', '#f97316', '#fdba74'
  ];

  const series = expectedIssues.map((expected, index) => {
    const referenceDate = new Date(cqlFechaMax);
    const targetDate = new Date(referenceDate);
    targetDate.setUTCDate(referenceDate.getUTCDate() - expected.offsetDays);
    targetDate.setUTCHours(expected.hourUTC, 0, 0, 0);

    const matchingIssue = Object.keys(issueDataMap).find(issueTs => {
      const issueDate = new Date(issueTs);
      return issueDate.getTime() === targetDate.getTime();
    });

    const dataKey = matchingIssue || `missing-${expected.label}`;
    const isMissing = dataKey.startsWith('missing-');

    return {
      dataKey,
      myLabel: expected.label,
      color: palette[index % palette.length],
      isMissing
    };
  });

  const dataset = fixedHours.map(item => {
    const row = { ...item };
    series.forEach(s => {
      const issue = s.dataKey;
      row[issue] =
        issueDataMap[issue] && issueDataMap[issue][item.hour] !== undefined
          ? issueDataMap[issue][item.hour]
          : null;
    });
    return row;
  });

  return { dataset, series };
};

const formatAndAddHours = (isoString) => {
  if (!isoString) return 'Unknown Date';
  const date = new Date(isoString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.00`;
};

const calculateYAxisRange = (dataset, marginPercentage = 0.1) => {
  const values = [];
  dataset.forEach(item => {
    Object.keys(item).forEach(key => {
      if (key !== 'hour' && key !== 'hourLabel') {
        const value = item[key];
        if (value !== null && value !== undefined) values.push(value);
      }
    });
  });
  if (values.length === 0) return { min: 0, max: 100 };
  const min = Math.min(...values);
  const max = Math.max(...values);
  const margin = (max - min) * marginPercentage;
  return { min: Math.max(0, min - margin), max: max + margin };
};

function groupSeries(series) {
  const groups = { 'D-0': [], 'D-1': [], 'D-2': [], 'STORM FREQUENCY': [] };
  series.forEach((s) => {
    if (s.dataKey.startsWith('storm-')) groups['STORM FREQUENCY'].push(s);
    else if (s.myLabel.startsWith('D-0')) groups['D-0'].push(s);
    else if (s.myLabel.startsWith('D-1')) groups['D-1'].push(s);
    else if (s.myLabel.startsWith('D-2')) groups['D-2'].push(s);
  });
  return groups;
}

function getYAxisConfig(metric) {
  const percentageCfg = { formatter: (v) => `${v.toFixed(1)}%`, isPct: true };
  switch (metric) {
    case 'FAR':
    case 'FAR_n1m':
      return { label: 'FAR score', formatter: v => v.toFixed(2), isPct: false };
    case 'GSS':
    case 'GSS_n1m':
      return { label: 'GSS score', formatter: v => v.toFixed(2), isPct: false };
    case 'F1':
    case 'F1_n1m':
      return { label: 'F1 score', formatter: v => v.toFixed(2), isPct: false };
    case 'RECALL':
    case 'RECALL_n1m':
      return { label: 'Recall score', formatter: v => v.toFixed(2), isPct: false };
    case 'PRECISION':
    case 'PRECISION_n1m':
      return { label: 'Precision score', formatter: v => v.toFixed(2), isPct: false };
    case 'HITS_rel':
    case 'HITS_rel_n1m':
      return { label: 'HITS score (%)', ...percentageCfg };
    case 'MISSES_rel':
    case 'MISSES_rel_n1m':
      return { label: 'MISSES score (%)', ...percentageCfg };
    default:
      return { label: 'Metric Value', ...percentageCfg };
  }
}

const toApiSeverity = (uiRisk) => {
  switch (uiRisk) {
    case 'Low': return 'Low';
    case 'Medium': return 'Medium';
    case 'High': return 'High';
    default: return uiRisk;
  }
};

// util de nombre bonito para capa
const prettyLayer = (layer) => {
  if (!layer) return '—';
  if (layer.includes('EU_025')) return 'Convection';
  if (layer.includes('EU_01')) return 'Convection High Res';
  return layer.split(':').pop().replace('_', ' ');
};

const metricNiceName = (m) =>
  m.replace('_rel', '').replace('_n1m', '').replace('RECALL', 'Recall');

/* ================= Componente ================= */
function TemporalGraph({
  cqlFechaMax,
  cqlFechaMin,
  cqlSevere,
  weekNumber,
  metricsNames,
  issueTime,
  handleRiskClick,
  mapLeftLayer,
}) {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

  const [data, setData] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState('HITS_rel');
  const [error, setError] = useState(null);
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const [visibleSeries, setVisibleSeries] = useState({});
  const initialUiRisk =
    cqlSevere === 'High' || cqlSevere === 'Severe' ? 'High'
      : cqlSevere === 'Low' ? 'Low'
        : 'Medium';
  const [uiRisk, setUiRisk] = useState(initialUiRisk);
  const [internalCqlSevere, setInternalCqlSevere] = useState(toApiSeverity(initialUiRisk));
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false); // expandir gráfica
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));


  // toggles
  const [N1Mmetrics, setN1Mmetrics] = useState(false);
  const [showStormFrequencyLine, setShowStormFrequencyLine] = useState(true);

  // menú de métricas (botón mejorado)
  const [metricPanelOpen, setMetricPanelOpen] = useState(false);
  const [metricAnchorPos, setMetricAnchorPos] = useState({ top: 12, left: 12 });
  const openMetricPanel = () => {
    if (typeof window !== "undefined") {
      setMetricAnchorPos({ top: 12, left: window.innerWidth - 12 }); // top-right
    }
    setMetricPanelOpen(true);
  };
  const closeMetricPanel = () => setMetricPanelOpen(false);

  // Helper: pick base metric and apply n1m suffix if needed
  const onPickBaseMetric = (baseValue) => {
    const final = N1Mmetrics ? `${baseValue}_n1m` : baseValue;
    setSelectedMetric(final);
    closeMetricPanel();
  };
  useEffect(() => {
    // Cierra el panel de métricas si está abierto
    if (expanded) setMetricPanelOpen(false);

    // Bloquea scroll del body al estar en "fullscreen"
    if (typeof document !== "undefined") {
      const prev = document.body.style.overflow || "";
      document.body.style.overflow = expanded ? "hidden" : prev;
      return () => { document.body.style.overflow = prev; };
    }
  }, [expanded, setMetricPanelOpen]);


  useEffect(() => {
    const fetchDataAndTransform = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/temporalGraph?` +
          `cqlFechaMax=${encodeURIComponent(cqlFechaMax)}` +
          `&cqlFechaMin=${encodeURIComponent(cqlFechaMin)}` +
          `&cqlSevere=${encodeURIComponent(internalCqlSevere)}` +
          `&weekNumber=${encodeURIComponent(weekNumber)}` +
          `&metricsNames=${encodeURIComponent(metricsNames)}` +
          `&issueTime=${encodeURIComponent(issueTime)}`
        );
        // Comprobamos errores HTTP
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const results = await response.json();
        const sorted = results.sort((a, b) => new Date(a.VALID_TIMESTAMP) - new Date(b.VALID_TIMESTAMP));

        if (sorted.length > 0) {
          setMinDate(formatAndAddHours(sorted[0].VALID_TIMESTAMP));
          setMaxDate(formatAndAddHours(sorted[sorted.length - 1].VALID_TIMESTAMP));
        } else {
          setMinDate('Unknown Date');
          setMaxDate('Unknown Date');
        }

        setData(transformForChart(sorted, mapLeftLayer));
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Could not load temporal data. Please try again.');
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDataAndTransform();
  }, [cqlFechaMax, cqlFechaMin, weekNumber, metricsNames, issueTime, internalCqlSevere, mapLeftLayer]);

  const { dataset: mainDataset, series: mainSeries } = useMemo(
    () => pivotChartData(data, selectedMetric, cqlFechaMax),
    [data, selectedMetric, cqlFechaMax]
  );

  const stormMetricKey = N1Mmetrics ? "STORM_FREQUENCY_n1m" : "STORM_FREQUENCY";
  const { dataset: stormDataset, series: stormPivotSeries } = useMemo(
    () => pivotChartData(data, stormMetricKey, cqlFechaMax),
    [data, stormMetricKey, cqlFechaMax]
  );

  const rawStormSeries = useMemo(
    () => stormPivotSeries.filter(s => s.myLabel === "D-1 19 UTC"),
    [stormPivotSeries]
  );

  const annotatedStormSeries = useMemo(
    () => rawStormSeries.map(s => ({
      ...s,
      dataKey: `storm-${s.dataKey}`,
      myLabel: `STORM FREQUENCY`,
      color: theme.palette.mode === 'dark' ? '#f87171' : '#dc2626'
    })),
    [rawStormSeries, theme.palette.mode]
  );

  useEffect(() => {
    setVisibleSeries(prevVisible => {
      const newVisible = { ...prevVisible };
      mainSeries.forEach(s => {
        if (!(s.dataKey in newVisible)) {
          newVisible[s.dataKey] = ["D-0 19 UTC", "D-1 19 UTC", "D-2 19 UTC"].includes(s.myLabel);
        }
      });
      annotatedStormSeries.forEach(s => {
        if (!(s.dataKey in newVisible)) newVisible[s.dataKey] = true;
      });
      return newVisible;
    });
  }, [mainSeries, annotatedStormSeries]);

  const combinedSeries = useMemo(() => {
    const mainVisible = mainSeries.filter(s => visibleSeries[s.dataKey]);
    const stormsVisible = showStormFrequencyLine
      ? annotatedStormSeries.filter(s => visibleSeries[s.dataKey])
      : [];
    return [...mainVisible, ...stormsVisible];
  }, [mainSeries, annotatedStormSeries, visibleSeries, showStormFrequencyLine]);

  const mainYAxisRange = useMemo(() => calculateYAxisRange(mainDataset), [mainDataset]);

  const stormYAxisRange = useMemo(() => {
    if (!showStormFrequencyLine) return { min: 0, max: 0.05 };
    const range = calculateYAxisRange(stormDataset);
    // Aseguramos que el rango mínimo sea al menos 0.05 para visibilidad
    const adjustedMax = Math.max(range.max, 0.05);
    return { min: 0, max: adjustedMax };
  }, [stormDataset, showStormFrequencyLine]);

  const chartData = useMemo(() => {
    const labels = mainDataset.map(d => d.hourLabel);

    // gradiente por serie (sutil)
    const gradientFor = (ctx, color) => {
      const { chart } = ctx;
      const { ctx: c, chartArea } = chart;
      if (!chartArea) return color;
      const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
      g.addColorStop(0, alpha(color, 0.18));
      g.addColorStop(1, alpha(color, 0.04));
      return g;
    };

    const datasets = combinedSeries.map((s) => {
      const isStorm = s.dataKey.startsWith('storm-');
      const sourceDataset = isStorm ? stormDataset : mainDataset;
      const rawKey = isStorm ? s.dataKey.replace('storm-', '') : s.dataKey;

      const dataValues = sourceDataset.map(d =>
        d[rawKey] !== undefined ? d[rawKey] : null
      );

      return {
        label: s.myLabel,
        data: dataValues,
        yAxisID: isStorm ? 'yStorm' : 'yMain',
        fill: isStorm ? false : 'origin',
        borderColor: s.color,
        backgroundColor: (ctx) => (isStorm ? 'transparent' : gradientFor(ctx, s.color)),
        borderWidth: isStorm ? 2.5 : 2,
        borderDash: isStorm ? [8, 5] : undefined,
        pointStyle: isStorm ? 'rectRot' : 'circle',
        pointRadius: isStorm ? 2.5 : 3,
        pointHoverRadius: isStorm ? 6 : 5,
        tension: 0.28
      };
    });

    return { labels, datasets };
  }, [mainDataset, stormDataset, combinedSeries]);

  const { label: mainYAxisLabel, formatter: mainTickFormatter, isPct } = useMemo(
    () => getYAxisConfig(selectedMetric),
    [selectedMetric]
  );

  /* -------- estilos para chips / tonos -------- */
  const chipTone = (theme.palette.mode === 'dark')
    ? alpha('#93c5fd', 0.12)
    : alpha(theme.palette.primary.main, 0.06);

  const ChipLabel = ({ tiny, bold, children }) => (
    <Typography
      component="span"
      variant={tiny ? 'caption' : 'body2'}
      sx={{ fontWeight: bold ? 700 : 500, opacity: tiny ? 0.75 : 1 }}
    >
      {children}
    </Typography>
  );

  // Botón "Métricas" mejorado (en lugar de <Select/>)
  const baseMetricOptions = [
    { value: 'F1', label: 'F1' },
    { value: 'RECALL', label: 'Recall' },
    { value: 'PRECISION', label: 'Precision' },
    { value: 'FAR', label: 'FAR' },
    { value: 'HITS_rel', label: 'HITS' },
    { value: 'MISSES_rel', label: 'MISSES' },
    { value: 'GSS', label: 'GSS' },
  ];
  const metricOptions = baseMetricOptions.map(opt => ({
    value: N1Mmetrics ? `${opt.value}_n1m` : opt.value,
    label: opt.label
  }));

  useEffect(() => {
    setSelectedMetric(prev =>
      N1Mmetrics ? (prev.endsWith('_n1m') ? prev : `${prev}_n1m`) : (prev.endsWith('_n1m') ? prev.replace('_n1m', '') : prev)
    );
  }, [N1Mmetrics]);

  const fullSeriesForPanel = useMemo(() => [...mainSeries, ...annotatedStormSeries], [mainSeries, annotatedStormSeries]);
  const groupedSeries = useMemo(() => groupSeries(fullSeriesForPanel), [fullSeriesForPanel]);

  /* ================= Estilos UI ================= */

  const MainContainer = styled(Box)({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: '12px',
    gap: '12px',
    backgroundColor: theme.palette.mode === 'dark' ? '#0b1220' : '#f8fafc'
  });

  const MetricCard = styled(Card)({
    borderRadius: 12,
    boxShadow: theme.palette.mode === 'dark' ? '0 4px 14px rgba(0,0,0,0.6)' : '0 4px 14px rgba(0,0,0,0.08)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`
  });

  const CardHeaderStyled = styled(CardHeader)({
    background: theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.08)' : 'linear-gradient(180deg, rgba(2,6,23,0.03), rgba(2,6,23,0))',
    padding: '10px 14px',
    '& .MuiCardHeader-title': {
      fontSize: '0.9rem',
      fontWeight: 700,
      color: theme.palette.text.primary,
      textAlign: 'center',
      letterSpacing: 0.2
    }
  });

  const HeaderPaper = styled(Paper)({
    borderRadius: 12,
    padding: 16,
    background: theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.06)' : '#ffffff',
    border: `1px solid ${theme.palette.divider}`
  });

  /* ================= Render ================= */

  const dateRangeLabel = (minDate && maxDate)
    ? `Time range · ${minDate} → ${maxDate} (UTC)`
    : '—';

  // alturas responsivas reales sin "romper" nada
  const chartMin = expanded ? 520 : 340;
  const chartMinResponsive = isSmDown ? (expanded ? 480 : 300) : chartMin;
  const chartMaxResponsive = expanded ? '78vh' : (isMdDown ? '62vh' : '60vh');

  // estilos del grupo de riesgo para que se note “conectado” al control global del dashboard
  const Segmented = styled(ToggleButtonGroup)(({ theme }) => ({
    borderRadius: 10,
    overflow: 'hidden',
    border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
    '& .MuiToggleButton-root': {
      textTransform: 'none',
      fontWeight: 600,
      padding: '6px 10px',
      lineHeight: 1.1,
      border: 'none',
      borderRight: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
      '&:last-of-type': { borderRight: 'none' }
    }
  }));

  const riskColor = (val /* 'Light' | 'Medium' | 'High' */) => {
    switch (val) {
      case 'Low': return '#ffff00'; // amarillo
      case 'Medium': return '#ff9500'; // naranja
      case 'High': return '#eb3f38'; // rojo
      default: return theme.palette.primary.main;
    }
  };

  const onRiskChange = (_, value) => {
    if (!value) return;
    setUiRisk(value);
    const apiSev = toApiSeverity(value);
    setInternalCqlSevere(apiSev);
    if (typeof handleRiskClick === 'function') handleRiskClick(apiSev);
  };

  const chartOptions = useMemo(() => {
    const fg = theme.palette.mode === 'dark' ? '#e5e7eb' : '#334155';
    const subfg = theme.palette.mode === 'dark' ? '#cbd5e1' : '#64748b';
    const grid = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: isSmDown ? 'bottom' : 'top',
          labels: {
            padding: 12,
            usePointStyle: true,
            color: subfg,
            boxWidth: 10,
            boxHeight: 10
          }
        },
        tooltip: {
          backgroundColor: theme.palette.mode === 'dark' ? '#0b1220' : '#ffffff',
          titleColor: fg,
          bodyColor: fg,
          borderColor: theme.palette.divider,
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          callbacks: {
            title: (items) => {
              // título claro: hora y rango no se repite (solo arriba)
              const hour = items[0]?.label ?? '';
              return `Hora (UTC): ${hour}`;
            },
            label: (ctx) => {
              const y = ctx.parsed.y;
              const series = ctx.dataset.label || '';
              if (y == null) return `${series}: —`;
              return isPct ? `${series}: ${y.toFixed(1)}%` : `${series}: ${y.toFixed(2)}`;
            }
          }
        },
        title: { display: false }
      },
      scales: {
        yMain: {
          type: 'linear',
          position: 'left',
          min: Math.max(0, mainYAxisRange.min),
          max: mainYAxisRange.max,
          grid: { display: true, color: grid, drawBorder: false },
          ticks: { color: subfg, callback: mainTickFormatter },
          title: { display: true, text: mainYAxisLabel, color: subfg }
        },
        yStorm: showStormFrequencyLine
          ? {
            type: 'linear',
            position: 'right',
            min: stormYAxisRange.min,
            max: stormYAxisRange.max,
            grid: { display: false, drawBorder: false },
            ticks: {
              callback: (val) => (val * 100).toFixed(1) + '%',
              color: theme.palette.mode === 'dark' ? '#fca5a5' : '#dc2626'
            },
            title: {
              display: true,
              text: `${uiRisk} Ground Truth Occupancy (%)`,
              color: theme.palette.mode === 'dark' ? '#fca5a5' : '#dc2626'
            }
          }
          : undefined,
        x: {
          grid: { display: true, color: grid, drawBorder: false },
          title: {
            display: true,
            text: 'Hour of day (UTC)', // NO repetimos el rango de fechas aquí
            color: subfg
          },
          ticks: { color: subfg, autoSkip: true, maxRotation: 0 }
        }
      },
      elements: {
        line: { borderWidth: 2 },
        point: { borderWidth: 2, hoverBorderWidth: 2 }
      },
      layout: { padding: { left: 4, right: 8, top: 0, bottom: isSmDown ? 8 : 0 } }
    };
  }, [theme.palette.mode, theme.palette.divider, mainYAxisRange, mainYAxisLabel, mainTickFormatter, isPct, showStormFrequencyLine, uiRisk, isSmDown]);

  const toggleSeries = (dataKey) => {
    setVisibleSeries(prev => ({ ...prev, [dataKey]: !prev[dataKey] }));
  };

  const resetVisibility = () => {
    const fresh = {};
    mainSeries.forEach(s => {
      fresh[s.dataKey] = ["D-0 19 UTC", "D-1 19 UTC", "D-2 19 UTC"].includes(s.myLabel);
    });
    annotatedStormSeries.forEach(s => { fresh[s.dataKey] = true; });
    setVisibleSeries(fresh);
  };

  return (
    <MainContainer>
      <Box
        sx={{
          height: '100%',         // Obligamos a que mida el alto de la pantalla
          overflowY: 'auto',       // Habilitamos scroll vertical
          display: 'flex',
          flexDirection: 'column',
          p: { xs: 1, sm: 2 },
          gap: 2,
          boxSizing: 'border-box' // Importante para que el padding no sume al tamaño
        }}
      >
        {/* Encabezado con título y chips mejorados */}
        <HeaderPaper sx={{ display: expanded ? 'none' : 'block' }}>
          {/* GRID maestro: 2 filas en md+ */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
              gridTemplateAreas: {
                xs: `"title" "filters" "actions"`,
                md: `"title title"
             "filters actions"`,
              },
              alignItems: "center",
              rowGap: { xs: 1, sm: 1.25 },
              columnGap: { md: 1.5 },
              width: "100%",
            }}
          >
            {/* === Fila 1: Título + chips (ocupa todo el ancho) === */}
            <Box
              gridArea="title" sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <ShowChartRoundedIcon fontSize="small" />
                <Typography
                  variant="h6"
                  fontWeight={800}
                  noWrap
                  title={`${metricNiceName(selectedMetric)} hourly profile`}
                  sx={{ minWidth: 0, fontSize: { xs: 16, sm: 18 } }}
                >
                  {metricNiceName(selectedMetric)} hourly profile
                </Typography>
              </Stack>

              {/* Chips: SIEMPRE scroll horizontal, nunca se salen de su celda */}
              <Box
                sx={{
                  mt: 0.75,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  flexWrap: "nowrap",
                  overflowX: "auto",
                  maxWidth: "100%",
                  pb: 0.5,
                  "&::-webkit-scrollbar": { height: 6 },
                  "&::-webkit-scrollbar-thumb": { bgcolor: "action.hover", borderRadius: 8 },
                }}
              >
                <Chip
                  size="small"
                  icon={<CalendarMonthRoundedIcon />}
                  label={dateRangeLabel}
                  sx={{
                    bgcolor: chipTone,
                    border: `1px dashed ${alpha(theme.palette.primary.main, 0.28)}`,
                    flexShrink: 0,
                  }}
                />

                <Chip
                  size="small"
                  icon={<LayersRoundedIcon />}
                  label={prettyLayer(mapLeftLayer)}
                  sx={{
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.35)}`,
                    "& .MuiChip-icon": { color: theme.palette.info.main },
                    flexShrink: 0,
                  }}
                />

                {N1Mmetrics && (
                  <Chip
                    size="small"
                    label="Neighbourhood Active"
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* === Fila 2 (columna izquierda): Filtros === */}
            <Box
              gridArea="filters" sx={{ minWidth: 0, overflow: "hidden" }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: "stretch", sm: "center" }}
                spacing={1}
                flexWrap={{ xs: "wrap", lg: 'nowrap' }}
              >
                {/* Risk */}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                  <Tooltip title="Select the risk filter for the global metrics.">
                    <WarningAmberRoundedIcon
                      sx={{
                        bgcolor: riskColor(uiRisk),
                        color: "#000",
                        border: "2px solid #000",
                        borderRadius: "50%",
                        fontSize: 28,
                        p: "2px",
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                  <Segmented
                    exclusive
                    size="small"
                    value={uiRisk}
                    onChange={onRiskChange}
                    aria-label="Risk selector"
                    sx={{
                      borderRadius: 1.5,
                      boxShadow: `inset 0 0 0 2px ${alpha(riskColor(uiRisk), 0.12)}`,
                      "& .MuiToggleButton-root": { px: 1.1, fontWeight: 700 },
                      "& .MuiToggleButton-root.Mui-selected": {
                        backgroundColor: alpha(riskColor(uiRisk), 0.85),
                        color: theme.palette.mode === "dark" ? "#fff" : "#0b1220",
                      },
                    }}
                  >
                    <ToggleButton value="Low">Low</ToggleButton>
                    <ToggleButton value="Medium">Medium</ToggleButton>
                    <ToggleButton value="High">High</ToggleButton>
                  </Segmented>
                </Stack>

                {/* Metrics button + top-right minimal panel */}
                <Stack direction="row" alignItems="center" spacing={1} flexWrap={{ xs: "wrap", lg: 'nowrap' }} sx={{ minWidth: 0 }}>
                  <Box sx={{ flexShrink: 0 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<InsightsRoundedIcon />}
                      onClick={openMetricPanel}
                      aria-haspopup="menu"
                      aria-expanded={metricPanelOpen ? "true" : undefined}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 700,
                        textTransform: "none",
                        borderColor: theme.palette.divider,
                        bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#fff",
                        px: 1.25,
                      }}
                    >
                      Metric: {metricNiceName(selectedMetric)}
                    </Button>

                    {/* Minimal “Metrics” panel pinned to top-right */}
                    <Menu
                      open={metricPanelOpen}
                      onClose={closeMetricPanel}
                      anchorEl={null}
                      anchorReference="anchorPosition"
                      anchorPosition={metricAnchorPos}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                      PaperProps={{
                        elevation: 10,
                        sx: {
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                          bgcolor: theme.palette.mode === "dark" ? "rgba(12,16,24,.98)" : "#fff",
                          backdropFilter: "blur(6px)",
                          width: { xs: "calc(100vw - 24px)", sm: 380 },
                          maxWidth: "calc(100vw - 24px)",
                          p: 1.25
                        },
                      }}
                      MenuListProps={{ disablePadding: true }}
                    >
                      {/* Header */}
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: .2 }}>
                          Metrics
                        </Typography>
                        <IconButton size="small" onClick={closeMetricPanel} aria-label="Close metrics panel">
                          <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      {/* Neighborhood (n1m) pill with checkbox */}
                      <ToggleButton
                        value="n1m"
                        size="small"
                        selected={N1Mmetrics}
                        onChange={() => setN1Mmetrics(v => !v)}
                        aria-pressed={N1Mmetrics}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          borderRadius: 999,
                          px: 1.25,
                          gap: .5,
                          alignItems: "center",
                          color: theme.palette.mode === "dark" ? "#fff" : theme.palette.text.primary,
                          border: `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
                          bgcolor: N1Mmetrics ? alpha(theme.palette.success.main, 0.16) : "transparent",
                          "&.Mui-selected": { bgcolor: alpha(theme.palette.success.main, 0.24) },
                          mb: 1
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={N1Mmetrics}
                          readOnly
                          disableRipple
                          sx={{ p: 0, m: 0, pointerEvents: "none" }}
                        />
                        Show Neighborhood Metrics
                      </ToggleButton>

                      <Divider sx={{ my: 1 }} />

                      {/* Metric picker — compact, professional */}
                      <Box
                        role="group"
                        aria-label="Pick a metric"
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, minmax(0,1fr))",
                          gap: 0.75,
                        }}
                      >
                        {[
                          { value: "F1", label: "F1" },
                          { value: "RECALL", label: "Recall" },
                          { value: "PRECISION", label: "Precision" },
                          { value: "FAR", label: "FAR" },
                          { value: "HITS_rel", label: "HITS" },
                          { value: "MISSES_rel", label: "MISSES" },
                          { value: "GSS", label: "GSS" },
                        ].map((opt) => {
                          const isActive = selectedMetric.replace("_n1m", "") === opt.value;
                          return (
                            <ToggleButton
                              key={opt.value}
                              value={opt.value}
                              size="small"
                              selected={isActive}
                              onClick={() => onPickBaseMetric(opt.value)}
                              sx={{
                                textTransform: "none",
                                justifyContent: "space-between",
                                borderRadius: 1.5,
                                fontWeight: 700,
                                px: 1,
                                "&.Mui-selected": {
                                  bgcolor: alpha(theme.palette.primary.main, 0.14),
                                  borderColor: alpha(theme.palette.primary.main, 0.5),
                                },
                              }}
                            >
                              <span>{opt.label}</span>
                              {isActive && <CheckRoundedIcon sx={{ fontSize: 16, opacity: .9 }} />}
                            </ToggleButton>
                          );
                        })}
                      </Box>
                    </Menu>
                  </Box>

                  {/* Minimal pill toggle for Storm frequency (replaces checkbox) */}
                  <ToggleButton
                    value="storm-frequency"
                    selected={showStormFrequencyLine}
                    onChange={() => setShowStormFrequencyLine(v => !v)}
                    size="small"
                    aria-pressed={showStormFrequencyLine}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: '0.65rem',
                      borderRadius: 8,
                      px: 1.25,
                      gap: 0.25,
                      alignItems: "center",
                      border: `1px solid ${alpha('#ef4444', 0.35)}`,
                      color: theme.palette.error.main,
                      bgcolor: showStormFrequencyLine ? alpha(theme.palette.error.main, 0.10) : "transparent",
                      "&.Mui-selected": { bgcolor: alpha(theme.palette.error.main, 0.18) }
                    }}
                  >
                    {/* Checkbox solo visual (evita doble onChange) */}
                    {/* <Checkbox
          size="small"
          checked={showStormFrequencyLine}
          readOnly
          disableRipple
          sx={{ p: 0, m: 0, pointerEvents: "none" }}
        /> */}
                    <ThunderstormIcon sx={{ fontSize: 14 }} />
                    <Box component="span">Show storm frequency</Box>
                    <Tooltip title="If the red line is close to 0%, the storms were too few for robust metrics." sx={{ marginLeft: 10.5 }}>
                      <InfoOutlinedIcon sx={{ color: theme.palette.text.disabled, fontSize: "1rem" }} />
                    </Tooltip>
                  </ToggleButton>

                </Stack>
              </Stack>
            </Box>

            {/* === Row 2 (right column): Actions === */}
            <Box
              gridArea="actions"
              sx={{
                justifySelf: "end",
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Tooltip title={expanded ? "Collapse chart" : "Expand chart"}>
                <IconButton
                  onClick={() => setExpanded((v) => !v)}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#fff",
                  }}
                >
                  {expanded ? <CloseFullscreenRoundedIcon /> : <OpenInFullRoundedIcon />}
                </IconButton>
              </Tooltip>

              <Tooltip title="Reset series visibility">
                <span>
                  <IconButton
                    onClick={resetVisibility}
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#fff",
                    }}
                  >
                    <RestartAltRoundedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </HeaderPaper>

        {/* Contenedor principal */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flexShrink: 0, // Evita que este contenedor se intente comprimir
        }}>
          {/* Gráfico */}
          <Paper
            elevation={0}
            sx={{

              minHeight: chartMinResponsive,
              maxHeight: chartMaxResponsive,
              display: 'flex',
              flexDirection: 'column',
              p: { xs: 1.5, sm: 2 },
              borderRadius: 12,
              background: theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.06)' : '#ffffff',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.palette.mode === 'dark'
                ? 'inset 0 1px 0 rgba(255,255,255,0.02), 0 6px 18px rgba(0,0,0,0.45)'
                : 'inset 0 1px 0 rgba(255,255,255,0.6), 0 6px 18px rgba(15,23,42,0.08)'
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
              {loading ? (
                <Box sx={{ p: 2 }}>
                  <Skeleton variant="rounded" height={12} sx={{ mb: 1 }} />
                  <Skeleton variant="rounded" height={isSmDown ? (expanded ? 420 : 260) : (expanded ? 420 : 280)} />
                </Box>
              ) : error ? (
                <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="error" variant="body2">{error}</Typography>
                </Box>
              ) : (mainDataset.length === 0) ? (
                <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary" variant="body2">No hay datos para los filtros seleccionados.</Typography>
                </Box>
              ) : (
                <Line
                  data={chartData}
                  options={chartOptions}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%'
                  }}
                />
              )}

              {/* === Overlay interno, dentro de la gráfica === */}
              {expanded && (
                <Box
                  sx={{
                    position: 'absolute',
                    opacity: 0.65,
                    top: 2,
                    right: 2,
                    zIndex: 2,               // por encima del canvas
                    pointerEvents: 'none',   // el contenedor no intercepta eventos
                  }}
                >
                  <Tooltip title="Exit fullscreen & show header">
                    <IconButton
                      onClick={() => setExpanded(false)}
                      aria-label="Exit fullscreen"
                      sx={{
                        pointerEvents: 'auto',  // el botón sí intercepta
                        border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(17,24,39,0.92)' : 'rgba(255,255,255,0.95)',
                        color: theme.palette.text.primary,
                        backdropFilter: 'blur(6px)',
                        boxShadow: 3,
                        borderRadius: 2,
                        '&:hover': { bgcolor: theme.palette.action.hover }
                      }}
                    >
                      <CloseFullscreenRoundedIcon />
                      {/* o FullscreenExitRoundedIcon si lo prefieres */}
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Tarjetas / Leyenda avanzada */}
          {/* Tarjetas / Leyenda avanzada */}
          <Box sx={{ flex: '1 1 40%', minHeight: 0, overflowY: 'auto', pr: 0.5, flexShrink: 0 }}>
            <Box
              sx={{
                display: 'grid',
                // Agregamos justifyContent para centrar las columnas si no ocupan todo el ancho
                justifyContent: 'center',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(auto-fit, minmax(100px, 150px))', // auto-fit es clave para el centrado
                  md: 'repeat(auto-fit, minmax(120px,180px))',
                },
                // Si prefieres mantener tus md y lg fijos, usa justifyItems:
                // justifyItems: 'center', 
                gap: 2,
                pb: 2
              }}
            >
              {Object.entries(groupedSeries)
                .filter(([groupName]) => groupName !== 'STORM FREQUENCY')
                .map(([groupName, seriesList]) => (
                  <MetricCard key={groupName} sx={{ width: '100%', maxWidth: 300 }}>
                    {/* Añadimos un maxWidth para que no se estiren demasiado al centrarse */}
                    <CardHeaderStyled
                      title={
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, textAlign: 'center' }}>
                          {groupName}{['D-0', 'D-1', 'D-2'].includes(groupName) && ` (${getAdjustedDate(groupName, issueTime)})`}
                        </Typography>
                      }
                    />
                    <Divider />
                    <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <TableContainer sx={{ flex: 1, maxHeight: 220, overflowY: 'auto' }}>
                        <Table size="small" stickyHeader>
                          <TableBody>
                            {seriesList.map(s => (
                              <TableRow
                                key={s.dataKey}
                                hover={!s.isMissing}
                                onClick={!s.isMissing ? () => toggleSeries(s.dataKey) : undefined}
                                sx={{
                                  cursor: s.isMissing ? 'default' : 'pointer',
                                  opacity: s.isMissing ? 0.5 : 1.0,
                                  backgroundColor: !s.isMissing && visibleSeries[s.dataKey]
                                    ? (theme.palette.mode === 'dark' ? 'rgba(34,197,94,0.12)' : 'rgba(219, 234, 254, 0.6)')
                                    : 'inherit',
                                  borderLeft: !s.isMissing && visibleSeries[s.dataKey] ? `4px solid ${s.color}` : '4px solid transparent',
                                  transition: 'all 0.15s ease-in-out',
                                  '&:hover': { backgroundColor: !s.isMissing ? (theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.12)' : 'rgba(0,0,0,0.03)') : 'inherit' }
                                }}
                              >
                                <TableCell sx={{ border: 'none', pl: 1, py: 0.6 }}>
                                  <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>
                                    {s.myLabel}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ border: 'none', pr: 1, py: 0.6 }} align="center" width={40}>
                                  {!s.isMissing && (
                                    <Box
                                      sx={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: '50%',
                                        backgroundColor: s.color,
                                        margin: '0 auto',
                                        border: `2px solid ${theme.palette.mode === 'dark' ? '#0b1220' : '#fff'}`,
                                        boxShadow: '0 0 0 1px rgba(0,0,0,0.08)'
                                      }}
                                    />
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </MetricCard>
                ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </MainContainer>
  );
}

// helper para etiqueta de fecha en cards
function getAdjustedDate(groupName, issueTime) {
  if (!issueTime) return 'Unknown';
  const date = new Date(issueTime);
  const days = groupName === 'D-1' ? 1 : groupName === 'D-2' ? 2 : 0;
  return format(subDays(date, days), 'yyyy-MM-dd');
}

export default TemporalGraph;
